import type {
  BogglePosition,
  BoggleRunLedgerAcceptedWordPathStep,
} from '~/boggle/engine/standard/types'
import { RARE_LETTER_SET } from '~/boggle/config/boardDistributions/letterWeights'
import { isEffectiveGoldPathStep } from '~/boggle/engine/specialTiles'

const ENGLISH_VOWELS = new Set('AEIOU')

/** Count rare-letter tiles (J/X/Z/Q) in a path, including repeats. */
export function countRareLetterOccurrences(letters: readonly string[]): number {
  let count = 0
  for (const letter of letters) {
    if (RARE_LETTER_SET.has(letter.toUpperCase())) {
      count += 1
    }
  }
  return count
}

/**
 * English consonant helper for badge rules (Y treated as consonant).
 */
export function isEnglishConsonantCodePoint(letter: string): boolean {
  if (letter.length !== 1) {
    return false
  }
  const upper = letter.toUpperCase()
  return upper >= 'A' && upper <= 'Z' && !ENGLISH_VOWELS.has(upper)
}

/** Longest run of consecutive consonants in `word` (uppercase/lowercase ok). */
export function maxConsonantRunLength(word: string): number {
  let current = 0
  let best = 0
  for (const character of word) {
    if (isEnglishConsonantCodePoint(character)) {
      current += 1
      best = Math.max(best, current)
    } else {
      current = 0
    }
  }
  return best
}

/**
 * Maximal same-letter runs; counts a "repeated-letter group" when run length ≥ 2.
 * `BOOKKEEPER` → 3 (`OO`, `KK`, `EE`). `AAA` → 1.
 */
export function countAdjacentRepeatedLetterGroups(word: string): number {
  const upper = word.toUpperCase()
  let index = 0
  let groups = 0
  while (index < upper.length) {
    const letter = upper[index]!
    let run = 0
    while (index < upper.length && upper[index] === letter) {
      run += 1
      index += 1
    }
    if (run >= 2) {
      groups += 1
    }
  }
  return groups
}

export function isTartarSauceWord(word: string): boolean {
  const upper = word.toUpperCase()
  if (upper.length < 6 || upper.length % 2 !== 0) {
    return false
  }
  const half = upper.length / 2
  return upper.slice(0, half) === upper.slice(half)
}

/** True when every letter is in non-decreasing alphabetical order (AABB ok unless distinct). */
export function isAlphabeticalOrderWord(
  word: string,
  minLength = 1,
  requireDistinctLetters = false,
): boolean {
  const upper = word.toUpperCase()
  if (upper.length < minLength) {
    return false
  }
  if (requireDistinctLetters && new Set(upper).size !== upper.length) {
    return false
  }
  for (let index = 1; index < upper.length; index += 1) {
    if (upper[index]! < upper[index - 1]!) {
      return false
    }
  }
  return true
}

/** Longest run of consecutive alphabet letters (e.g. DEF, PQR). */
export function maxConsecutiveAlphabetRunLength(word: string): number {
  const upper = word.toUpperCase()
  let current = 0
  let best = 0
  for (let index = 0; index < upper.length; index += 1) {
    const code = upper.charCodeAt(index)
    if (code < 65 || code > 90) {
      current = 0
      continue
    }
    if (index > 0 && code === upper.charCodeAt(index - 1) + 1) {
      current += 1
    } else {
      current = 1
    }
    best = Math.max(best, current)
  }
  return best
}

/** Max visits to one board position with effective gold credit on each visit. */
export function maxSameGoldTileUsesInPath(
  pathSteps: readonly BoggleRunLedgerAcceptedWordPathStep[],
): number {
  const usesByPosition = new Map<string, number>()
  let best = 0
  for (const step of pathSteps) {
    if (!isEffectiveGoldPathStep(step)) {
      continue
    }
    const key = `${step.position.col},${step.position.row}`
    const next = (usesByPosition.get(key) ?? 0) + 1
    usesByPosition.set(key, next)
    best = Math.max(best, next)
  }
  return best
}

export function isPalindromeWord(word: string): boolean {
  const upper = word.toUpperCase()
  if (upper.length < 5) {
    return false
  }
  return upper === upper.split('').reverse().join('')
}

export function isFourTilesInA2x2Square(path: ReadonlyArray<BogglePosition>): boolean {
  if (path.length < 4) {
    return false
  }
  const cols = path.map(p => p.col)
  const rows = path.map(p => p.row)
  const minC = Math.min(...cols)
  const maxC = Math.max(...cols)
  const minR = Math.min(...rows)
  const maxR = Math.max(...rows)
  if (maxC - minC !== 1 || maxR - minR !== 1) {
    return false
  }
  for (const position of path) {
    if (
      position.col < minC ||
      position.col > maxC ||
      position.row < minR ||
      position.row > maxR
    ) {
      return false
    }
  }
  const key = (p: BogglePosition) => `${p.col},${p.row}`
  const cells = new Set(path.map(key))
  return (
    cells.has(`${minC},${minR}`) &&
    cells.has(`${maxC},${minR}`) &&
    cells.has(`${minC},${maxR}`) &&
    cells.has(`${maxC},${maxR}`)
  )
}

/** Path uses one full row, columns 0..boardCols-1 in order (left to right). */
export function isFullRowLeftToRightPath(
  path: ReadonlyArray<BogglePosition>,
  boardCols: number,
  boardRows: number,
): boolean {
  if (boardCols < 1 || boardRows < 1 || path.length !== boardCols) {
    return false
  }
  const row = path[0]!.row
  if (row < 0 || row >= boardRows) {
    return false
  }
  for (let col = 0; col < boardCols; col += 1) {
    const p = path[col]!
    if (p.row !== row || p.col !== col) {
      return false
    }
  }
  return true
}

export function isCornerPathWord(
  path: ReadonlyArray<BogglePosition>,
  boardCols: number,
  boardRows: number,
): boolean {
  if (path.length < 4 || boardCols < 1 || boardRows < 1) {
    return false
  }
  const cornerKeys = [
    `0,0`,
    `${boardCols - 1},0`,
    `0,${boardRows - 1}`,
    `${boardCols - 1},${boardRows - 1}`,
  ].filter(key => {
    const [c, r] = key.split(',').map(Number) as [number, number]
    return c >= 0 && r >= 0 && c < boardCols && r < boardRows
  })
  if (cornerKeys.length < 4) {
    return false
  }
  const corners = new Set(cornerKeys)
  for (const position of path) {
    if (!corners.has(`${position.col},${position.row}`)) {
      return false
    }
  }
  const cells = new Set(path.map(p => `${p.col},${p.row}`))
  for (const key of cornerKeys) {
    if (!cells.has(key)) {
      return false
    }
  }
  return true
}
